import { useRef, useState } from 'react';
import {
  isKitchenRoom,
  isValidRoomNumber,
  requiredPhotoCount,
  type InspectionType,
} from '@room-verify/shared';
import { ApiError, api, type Inspection } from '../api';
import { Button, IconCamera, IconDoor, IconKitchen, IconSparkle } from '../components';
import { errorMessage, type Copy } from '../i18n';

export function RoomEntryScreen({
  copy,
  token,
  inspectionType,
  onStarted,
  onAuthExpired,
}: {
  copy: Copy;
  token: string;
  inspectionType: InspectionType;
  onStarted: (inspection: Inspection) => void;
  onAuthExpired: () => void;
}) {
  const [roomNumber, setRoomNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const trimmed = roomNumber.trim();
  const valid = isValidRoomNumber(trimmed);
  const kitchen = valid && isKitchenRoom(trimmed);
  const photoCount = valid ? requiredPhotoCount(trimmed) : 0;

  const start = async () => {
    if (inFlight.current) return;
    if (!valid) {
      setError(copy.invalidRoomNumber);
      return;
    }

    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const { inspection } = await api.startInspection(token, trimmed, inspectionType);
      onStarted(inspection);
    } catch (caught) {
      if (caught instanceof ApiError && caught.isAuthExpiry) {
        onAuthExpired();
      } else {
        setError(errorMessage(copy, caught instanceof ApiError ? caught.code : 'NETWORK'));
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <div className="type-pill">
        {inspectionType === 'CHECKOUT' ? <IconDoor size={16} /> : <IconSparkle size={16} />}
        {inspectionType === 'CHECKOUT' ? copy.checkoutRooms : copy.cleanRooms}
      </div>

      <div className="card card-pad room-entry">
        <label className="field-label" htmlFor="room">
          {copy.roomNumber}
        </label>
        <input
          id="room"
          className="input room-input num"
          value={roomNumber}
          onChange={(event) => {
            // Digits only: the keypad, the validation and the server all agree.
            setRoomNumber(event.target.value.replace(/\D/g, '').slice(0, 4));
            setError(null);
          }}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          placeholder="000"
          enterKeyHint="go"
          aria-describedby="room-hint"
          aria-invalid={error ? true : undefined}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && valid) void start();
          }}
        />
        <p id="room-hint" className="field-hint">
          {copy.roomNumberHint}
        </p>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
      </div>

      {valid && (
        <div className="confirm-card" aria-live="polite">
          <p className="confirm-eyebrow">{copy.confirmTitle}</p>
          <p className="confirm-room">
            {copy.roomNumber} <strong className="num">{trimmed}</strong>
          </p>
          <p className="confirm-type">
            {inspectionType === 'CHECKOUT' ? copy.checkoutRooms : copy.cleanRooms}
          </p>

          <div className="confirm-meta">
            <span className="badge badge-neutral">
              <IconCamera size={13} />
              {copy.photosRequired(photoCount)}
            </span>
            {kitchen && (
              <span className="badge badge-accent">
                <IconKitchen size={13} />
                {copy.sectionKitchen}
              </span>
            )}
          </div>

          {kitchen && <p className="confirm-note">{copy.kitchenRoomNote}</p>}
        </div>
      )}

      <div className="sticky-actions">
        <Button
          variant="accent"
          size="lg"
          block
          disabled={!valid}
          loading={busy}
          onClick={start}
          icon={<IconCamera size={18} />}
        >
          {busy ? copy.starting : copy.startInspection}
        </Button>
      </div>
    </div>
  );
}
